import React, { useState, useEffect, lazy, Suspense } from 'react';
import { Linkedin, Mail, ArrowUpRight, Menu, X, Moon, Sun, ShieldCheck, Search, Sparkles } from 'lucide-react';
import Seo from './Seo.tsx';
import ContactForm from './ContactForm.tsx';
import PlaceholderImage from './PlaceholderImage.tsx';
import Clients from './Clients.tsx';
import Testimonials from './Testimonials.tsx';

interface Project {
  id: number;
  title: string;
  description: string;
  tags: string[];
  image: string;
  link?: string;
}

interface SkillGroup {
  category: string;
  items: string[];
}

const Portfolio: React.FC = () => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [visibleProjects, setVisibleProjects] = useState(6);
  const [activeSection, setActiveSection] = useState('');
  const [isDarkMode, setIsDarkMode] = useState(false);
  const [isLoaded, setIsLoaded] = useState(false);
  
  // BenBot is now implemented as a separate component
  
  // Featured projects data based on Ben's portfolio
  const projects: Project[] = [

    {
      id: 12,
      title: "The Palm Beaches",
      description: "Core developer on Palm Beach County's official destination site. Custom Gutenberg blocks, interactive maps, and event feeds the marketing team runs without a developer.",
      tags: ["WordPress", "Custom Gutenberg Blocks", "PHP", "Tourism Platform"],
      image: "/portfolio-images/palmbeaches.jpg",
      link: "https://www.thepalmbeaches.com"
    },
    {
      id: 1,
      title: "Speed Read AI",
      description: "Script coverage software for film and publishing professionals. Reads a screenplay or manuscript and returns full coverage in minutes. Founded and built by me, concept to launch.",
      tags: ["React", "Node.js", "AI/LLMs", "SaaS"],
      image: "/portfolio-images/speedread.jpg",
      link: "https://gospeedread.com"
    },
    {
      id: 13,
      title: "CPNRD Visitor Center Kiosk",
      description: "A touchscreen set into a sculptural tree in a visitor center lobby. Native app with cloud CMS sync, a custom watercolor animation engine, and built-in analytics. Design by McCullough Creative.",
      tags: ["Interactive Kiosk", "Custom Native App", "Watercolor Animation", "Offline-First", "Touch UI"],
      image: "/portfolio-images/cpnrd-tree.jpg",
      link: "/kiosks#case-studies"
    },
    {
      id: 15,
      title: "McCullough Creative",
      description: "Agency site built as a full-site-editing WordPress theme with a custom block library. Their team composes new pages from the blocks; the design system keeps everything on rails.",
      tags: ["WordPress", "Custom Gutenberg Blocks", "Full Site Editing", "PHP"],
      image: "/portfolio-images/mccullough.jpg",
      link: "https://shootforthemoon.com"
    },
    {
      id: 4,
      title: "Capra Bank",
      description: "WordPress site for a bank, with secure integrations to its banking services.",
      tags: ["WordPress", "JavaScript", "Security", "Financial APIs"],
      image: "/portfolio-images/capra.jpg",
      link: "https://caprabank.com"
    },
    {
      id: 2,
      title: "Discover Atlanta",
      description: "Senior developer on Atlanta's official visitor site: custom blocks, API integrations, and the publishing tools behind them.",
      tags: ["WordPress", "PHP", "Custom Gutenberg Blocks", "API Integrations"],
      image: "/portfolio-images/atlanta.jpg",
      link: "https://discoveratlanta.com"
    },
    {
      id: 14,
      title: "Overthinking the Awesome",
      description: "Podcast site for author David Cosgrove. Brand, on-site episode player with RSS, and a funnel to the companion book.",
      tags: ["WordPress", "Web Design", "Podcast Platform", "RSS Integration", "SEO"],
      image: "/portfolio-images/overthinking.jpg",
      link: "https://overthinkingtheawesome.com"
    },
    {
      id: 3,
      title: "University of Northern Iowa — 150th Anniversary Kiosks",
      description: "Interactive kiosks marking the university's 150th year. Offline-first Electron apps fed by WordPress, with a full accessibility suite. Design by McCullough Creative.",
      tags: ["Electron", "Interactive Kiosks", "WordPress", "Accessibility", "Touch UI"],
      image: "/portfolio-images/uni-kiosk.jpg",
      link: "/kiosks#case-studies"
    },
    {
      id: 5,
      title: "Splendor Water",
      description: "Shopify store with subscription products and a custom theme.",
      tags: ["Shopify", "E-commerce", "Subscription Model", "Custom Theme"],
      image: "/portfolio-images/splendor.jpg",
      link: "https://splendorwater.com"
    },
    {
      id: 6,
      title: "Boise State University",
      description: "WordPress plugins, LMS work, and interactive tools for university departments.",
      tags: ["WordPress", "Custom Plugins", "LMS", "Accessibility"],
      image: "/portfolio-images/boise.jpg"
    },
    {
      id: 7,
      title: "Osbourne Media House",
      description: "Media site for the Osbourne family: podcast feeds, YouTube sync, and a Shopify store.",
      tags: ["WordPress", "Media Integration", "E-commerce", "API Integrations"],
      image: "/portfolio-images/osbournes.jpg",
      link: "https://osbournemediahouse.com"
    },
    {
      id: 8,
      title: "AXIS Seed",
      description: "One WordPress platform serving AXIS Seed and ten-plus licensed subsidiaries, maintained year-round.",
      tags: ["WordPress", "Custom Plugins", "Multi-site Management"],
      image: "/portfolio-images/axis.jpg",
      link: "https://axisseed.com"
    },
    {
      id: 9,
      title: "Advanced Content Scheduler",
      description: "WordPress plugin that schedules content by date, time, and day of week.",
      tags: ["WordPress", "Plugin Development", "Content Management", "Premium Plugin"],
      image: "/portfolio-images/acs.jpg"
    },
    {
      id: 10,
      title: "Visit Buffalo Niagara",
      description: "Lead developer on the region's official tourism site.",
      tags: ["WordPress", "Tourism Platform", "Custom Integrations", "Responsive Design"],
      image: "/portfolio-images/buffalo.jpg",
      link: "https://visitbuffaloniagara.com"
    },
    {
      id: 11,
      title: "John Deere SmartDetect™ Kiosk",
      description: "Touch kiosk for John Deere's ConExpo 2026 booth. Native WinUI 3, multi-display, runs with no network.",
      tags: ["WinUI 3", "C#", "XAML", "Interactive Kiosk", "Trade Show"],
      image: "/portfolio-images/smartdetect-ui.jpg",
      link: "/kiosks"
    }
  ];

  // Skills data based on Ben's experience and resume
  const skills: SkillGroup[] = [
    { category: "Languages & Frontend", items: ["JavaScript", "React", "React Native", "PHP", "HTML/CSS", "SQL", "Python"] },
    { category: "Backend & Frameworks", items: ["Node.js", "Express", "WordPress (FSE, Headless, Gutenberg)", "REST APIs", "Custom Plugin Development"] },
    { category: "AI & LLM Technologies", items: ["RAG Systems", "Prompt Engineering", "LLM Fine-tuning", "Multi-agent Pipelines", "NLP"] },
    { category: "DevOps & Platforms", items: ["Git", "Netlify", "Vercel", "Docker", "cPanel", "Shopify", "Squarespace"] }
  ];

  // Toggle mobile menu
  const toggleMenu = () => {
    setIsMenuOpen(!isMenuOpen);
  };

  // Smooth scroll to section
  const scrollToSection = (sectionId: string, e: React.MouseEvent<HTMLAnchorElement>) => {
    e.preventDefault();
    const element = document.getElementById(sectionId);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
      // Update URL without triggering navigation
      window.history.pushState(null, '', `#${sectionId}`);
    }
  };

  // Trigger fade-in animation on mount
  useEffect(() => {
    setIsLoaded(true);
  }, []);

  // Initialize and track dark mode with system preference
  useEffect(() => {
    const stored = localStorage.getItem('darkMode');
    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    
    // Check if user has manually set a preference
    if (stored === 'true' || stored === 'false') {
      setIsDarkMode(stored === 'true');
    } else {
      // Otherwise use system preference
      setIsDarkMode(mediaQuery.matches);
    }

    // Listen for system preference changes
    const handleChange = (e: MediaQueryListEvent) => {
      // Only update if user hasn't manually set a preference
      const manualPreference = localStorage.getItem('darkMode');
      if (!manualPreference || manualPreference === 'system') {
        setIsDarkMode(e.matches);
      }
    };

    mediaQuery.addEventListener('change', handleChange);
    return () => mediaQuery.removeEventListener('change', handleChange);
  }, []);

  // Update dark mode class on document
  useEffect(() => {
    if (isDarkMode) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDarkMode]);

  // Toggle dark mode and save preference
  const toggleDarkMode = () => {
    const newValue = !isDarkMode;
    setIsDarkMode(newValue);
    // Store the manual preference
    localStorage.setItem('darkMode', newValue.toString());
  };

  // Track active section on scroll
  useEffect(() => {
    const handleScroll = () => {
      const sections = ['about', 'projects', 'clients', 'skills', 'contact'];
      const scrollPosition = window.scrollY + 100; // Offset for fixed nav

      for (const section of sections) {
        const element = document.getElementById(section);
        if (element) {
          const { offsetTop, offsetHeight } = element;
          if (scrollPosition >= offsetTop && scrollPosition < offsetTop + offsetHeight) {
            setActiveSection(section);
            break;
          }
        }
      }

      // Clear active section if at top of page
      if (window.scrollY < 100) {
        setActiveSection('');
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll(); // Check initial position

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Contact form is now handled by the ContactForm component
  
  // BenBot functionality now handled by BenBotChat component

  return (
    <div className={`min-h-screen bg-neutral-50 dark:bg-neutral-900 text-neutral-900 dark:text-neutral-100 font-sans ${isLoaded ? 'animate-fadeInUp' : 'opacity-0'}`}>
      <Seo
        title="Ben Smith | Interactive Installations & WordPress Development"
        description="Developer of touchscreen kiosks, interactive installations, and editorial WordPress platforms. 15 years and 150+ projects for agencies, universities, and brands including John Deere and the National Park Service."
        path="/"
      />

      {/* Skip to main content link for accessibility */}
      <a href="#main-content" className="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4 bg-primary-600 dark:bg-primary-500 text-white px-4 py-2 rounded-md z-50">
        Skip to main content
      </a>
      
      {/* Navigation */}
      <nav className="bg-white/80 dark:bg-neutral-900/80 backdrop-blur-md py-4 px-6 sticky top-0 shadow-soft z-50 border-b border-primary-100 dark:border-neutral-700">
        <div className="max-w-6xl mx-auto flex justify-between items-center">
          <a href="#" className="text-2xl font-bold">
            <span className="text-neutral-900 dark:text-neutral-100">Ben Smith</span>
            <span className="text-primary-600 dark:text-primary-400"> Development</span>
          </a>
          
          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-4 lg:space-x-6">
            <a
              href="#projects"
              onClick={(e) => scrollToSection('projects', e)}
              className={`${
                activeSection === 'projects' ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30' : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400'
              } transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5`}>
              Projects
            </a>
            <a
              href="/kiosks"
              className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5">
              Kiosks
            </a>
            <a
              href="/gutenberg"
              className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5">
              Gutenberg
            </a>
            <a
              href="/maintenance-and-seo"
              className="whitespace-nowrap text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5">
              Maintenance & SEO
            </a>
            <a
              href="/about"
              className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5">
              About
            </a>
            <a
              href="#contact"
              onClick={(e) => scrollToSection('contact', e)}
              className={`${
                activeSection === 'contact' ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30' : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400'
              } transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5`}>
              Contact
            </a>
            <button
              onClick={toggleDarkMode}
              className={`${
                activeSection === '' ? '' : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400'
              } transition-all duration-200 font-medium hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 rounded-md px-3 py-1.5 flex items-center space-x-2`}
              aria-label={isDarkMode ? "Switch to light mode" : "Switch to dark mode"}
            >
              {isDarkMode ? (
                <Sun size={18} />
              ) : (
                <Moon size={18} />
              )}
              <span className="hidden">Theme</span>
            </button>
          </div>
          
          {/* Mobile menu button */}
          <button 
              className="md:hidden p-2 hover:bg-primary-50 dark:hover:bg-neutral-800 rounded-lg transition-colors" 
              onClick={toggleMenu}
              aria-label={isMenuOpen ? "Close menu" : "Open menu"}
              aria-expanded={isMenuOpen}
            >
              {isMenuOpen ? <X size={24} className="text-neutral-700 dark:text-neutral-300" /> : <Menu size={24} className="text-neutral-700 dark:text-neutral-300" />}
            </button>
        </div>
        
        {/* Mobile Navigation */}
        {isMenuOpen && (
          <div className="md:hidden absolute top-16 left-0 right-0 bg-white/95 dark:bg-neutral-900/95 backdrop-blur-md border-b border-primary-100 dark:border-neutral-700 p-4 shadow-soft rounded-b-2xl z-50">
            <div className="flex flex-col space-y-4">
              <a
                href="#projects"
                onClick={(e) => { scrollToSection('projects', e); toggleMenu(); }}
                className={`${
                  activeSection === 'projects' ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30' : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400'
                } transition-colors font-medium px-3 py-2 rounded-md`}>
                Projects
              </a>
              <a
                href="/kiosks"
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md"
                onClick={toggleMenu}>
                Kiosks
              </a>
              <a
                href="/gutenberg"
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md"
                onClick={toggleMenu}>
                Gutenberg
              </a>
              <a
                href="/maintenance-and-seo"
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md"
                onClick={toggleMenu}>
                Maintenance & SEO
              </a>
              <a
                href="/about"
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md"
                onClick={toggleMenu}>
                About
              </a>
              <a
                href="#contact"
                onClick={(e) => { scrollToSection('contact', e); toggleMenu(); }}
                className={`${
                  activeSection === 'contact' ? 'text-primary-600 dark:text-primary-400 bg-primary-50 dark:bg-primary-900/30' : 'text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400'
                } transition-colors font-medium px-3 py-2 rounded-md`}>
                Contact
              </a>
              <button
                onClick={() => {
                  toggleDarkMode();
                  toggleMenu();
                }}
                className="text-neutral-700 dark:text-neutral-300 hover:text-primary-600 dark:hover:text-primary-400 transition-colors font-medium px-3 py-2 rounded-md flex items-center space-x-2"
              >
                {isDarkMode ? (
                  <Sun size={18} />
                ) : (
                  <Moon size={18} />
                )}
                <span>Theme</span>
              </button>
            </div>
          </div>
        )}
      </nav>

      {/* Hero Section */}
      <main id="main-content">
      <section className="relative bg-gradient-to-br from-primary-500 via-primary-700 to-primary-900 dark:from-primary-600 dark:via-primary-800 dark:to-neutral-900 text-white py-24 px-6 overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-tr from-accent-600/20 dark:from-accent-500/20 to-transparent"></div>
        <div className="relative max-w-6xl mx-auto flex flex-col items-center text-center z-10">
          <h1 className="text-4xl md:text-6xl font-bold mb-8 max-w-4xl">
            Senior <span className="text-accent-400 dark:text-accent-300">developer for agencies</span> and the businesses they serve.
          </h1>
          <p className="text-lg md:text-xl max-w-3xl mb-10 text-primary-50/90 dark:text-primary-100/80">
            Fifteen years of WordPress platforms, custom applications, and interactive installations, plus the ongoing care, maintenance, search, and LLM optimization work that keeps a site delivering its full value.
          </p>
          <div className="flex space-x-4">
            <a href="#projects" className="bg-white dark:bg-primary-100 text-primary-700 dark:text-primary-800 font-semibold py-3 px-8 rounded-full hover:shadow-lg hover:scale-105 transition-all duration-300 flex items-center group">
              See the work <ArrowUpRight size={18} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
            <a href="#contact" className="bg-transparent border-2 border-white/80 dark:border-white/60 text-white font-semibold py-3 px-8 rounded-full hover:bg-white/10 dark:hover:bg-white/20 hover:border-white dark:hover:border-white/80 hover:shadow-soft transition-all duration-300">
              Get in touch
            </a>
          </div>
        </div>
      </section>

      {/* Projects Section */}
      <section id="projects" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">Selected work</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6 lg:gap-8">
            {projects.slice(0, visibleProjects).map(project => (
              <div key={project.id} className="group bg-white dark:bg-neutral-800 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-2 border border-primary-100 dark:border-neutral-700 flex flex-col h-full">
                {project.link && project.link.startsWith('/') ? (
                  <a href={project.link} className="w-full h-48 overflow-hidden bg-neutral-100 dark:bg-neutral-700 flex-shrink-0 block">
                    {project.image ?
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      style={{objectPosition: 'top center'}}
                      loading="lazy"
                      />
                    :
                    <PlaceholderImage width={600} height={400} text={project.title} />}
                  </a>
                ) : (
                  <div className="w-full h-48 overflow-hidden bg-neutral-100 dark:bg-neutral-700 flex-shrink-0">
                    {project.image ?
                    <img
                      src={project.image}
                      alt={project.title}
                      className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-110"
                      style={{objectPosition: 'top center'}}
                      loading="lazy"
                      />
                    :
                    <PlaceholderImage width={600} height={400} text={project.title} />}
                  </div>
                )}
                <div className="p-5 flex flex-col flex-grow">
                  {project.link && project.link.startsWith('/') ? (
                    <a href={project.link}>
                      <h3 className="text-lg font-bold mb-2 dark:text-neutral-100 hover:text-primary-600 dark:hover:text-primary-400 transition-colors">{project.title}</h3>
                    </a>
                  ) : (
                    <h3 className="text-lg font-bold mb-2 dark:text-neutral-100">{project.title}</h3>
                  )}
                  <p className="text-neutral-600 dark:text-neutral-400 mb-3 text-sm leading-relaxed flex-grow">{project.description}</p>
                  <div className="flex flex-wrap gap-1.5 mb-4">
                    {project.tags.map((tag, index) => (
                      <span key={index} className="bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs px-2.5 py-0.5 rounded-full font-medium">
                        {tag}
                      </span>
                    ))}
                  </div>
                  <div className="mt-auto">
                    {project.link ? (
                      <a
                        href={project.link}
                        target={project.link.startsWith('/') ? undefined : "_blank"}
                        rel={project.link.startsWith('/') ? undefined : "noopener noreferrer"}
                        className="text-primary-600 dark:text-primary-400 font-semibold flex items-center hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-200 group"
                      >
                        View project <ArrowUpRight size={18} className="ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </a>
                    ) : (
                      <a
                        href="#contact"
                        className="text-primary-600 dark:text-primary-400 font-semibold flex items-center hover:text-accent-600 dark:hover:text-accent-400 transition-all duration-200 group"
                      >
                        Ask me about this <ArrowUpRight size={18} className="ml-1 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
          {projects.length > visibleProjects && (
            <div className="text-center mt-12">
              <button
                onClick={() => setVisibleProjects(prev => prev + 6)}
                className="inline-block border-2 border-primary-600 dark:border-primary-400 text-primary-600 dark:text-primary-400 font-semibold py-3 px-8 rounded-full hover:bg-primary-600 dark:hover:bg-primary-500 hover:text-white dark:hover:text-white hover:shadow-lg transition-all duration-300 hover:scale-105"
              >
                More projects
              </button>
            </div>
          )}
        </div>
      </section>

      {/* Clients Section */}
      <section id="clients" className="bg-white dark:bg-neutral-800">
        <Clients />
      </section>

      {/* Interactive Kiosks Showcase */}
      <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">
            Kiosks & installations
          </h2>
          {/* Intro text */}
          <div className="max-w-3xl mx-auto text-center mb-12">
            <p className="text-lg text-neutral-700 dark:text-neutral-200 leading-relaxed mb-8">
              I build kiosk software and interactive installations for trade shows, museums, and visitor centers. Offline-first, multilingual, accessible — made to run unattended in public.
            </p>
            <div className="flex flex-wrap justify-center gap-3">
              {['Multilingual', 'Offline-First', 'WCAG Accessible'].map((stat) => (
                <span key={stat} className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm px-4 py-2 rounded-full font-medium border border-primary-200 dark:border-primary-700/50">
                  {stat}
                </span>
              ))}
            </div>
          </div>

          {/* Project preview cards */}
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-12">
            <a href="/kiosks#case-studies" className="group block bg-white dark:bg-neutral-700 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100 dark:border-neutral-600">
              <div className="h-48 overflow-hidden">
                <img
                  src="/portfolio-images/cpnrd-tree.jpg"
                  alt="CPNRD visitor center kiosk built into a sculptural wooden tree"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{objectPosition: 'center center'}}
                />
              </div>
              <div className="p-4">
                <div className="text-primary-600 dark:text-primary-400 text-xs font-semibold tracking-wide uppercase mb-1">Central Platte NRD</div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Visitor Center Kiosk</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">Touchscreen built into a sculptural tree, with a custom watercolor animation engine</p>
              </div>
            </a>
            <a href="/kiosks#case-studies" className="group block bg-white dark:bg-neutral-700 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100 dark:border-neutral-600">
              <div className="h-48 overflow-hidden">
                <img
                  src="/portfolio-images/uni-installation.jpg"
                  alt="University of Northern Iowa 150th Anniversary Kiosk Installation"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{objectPosition: 'center center'}}
                />
              </div>
              <div className="p-4">
                <div className="text-primary-600 dark:text-primary-400 text-xs font-semibold tracking-wide uppercase mb-1">University of Northern Iowa</div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white">150th Anniversary Kiosks</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">Multi-kiosk Electron installation with full accessibility suite</p>
              </div>
            </a>
            <a href="/kiosks" className="group block bg-white dark:bg-neutral-700 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100 dark:border-neutral-600">
              <div className="h-48 overflow-hidden">
                <img
                  src="/portfolio-images/smartdetect-environment.jpg"
                  alt="John Deere SmartDetect kiosk in use on the ConExpo trade show floor"
                  className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                  style={{objectPosition: 'center center'}}
                />
              </div>
              <div className="p-4">
                <div className="text-primary-600 dark:text-primary-400 text-xs font-semibold tracking-wide uppercase mb-1">John Deere</div>
                <h3 className="text-lg font-bold text-neutral-900 dark:text-white">SmartDetect™ Trade Show Kiosk</h3>
                <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">Native WinUI 3 touch kiosk for ConExpo 2026</p>
              </div>
            </a>
          </div>

          {/* CTA */}
          <div className="text-center">
            <a
              href="/kiosks"
              className="inline-flex items-center bg-primary-600 dark:bg-primary-500 text-white font-semibold py-3 px-8 rounded-full hover:bg-primary-700 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all duration-300 group"
            >
              See kiosk work
              <ArrowUpRight size={18} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
            </a>
          </div>
        </div>
      </section>

      {/* Gutenberg Development Showcase */}
      <section className="py-24 px-6 bg-white dark:bg-neutral-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">
            Custom Gutenberg blocks
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-10 lg:gap-14 items-center">
            {/* Left column - Description & CTA (order-2 on mobile so images come first) */}
            <div className="order-2 lg:order-1">
              <p className="text-lg text-neutral-700 dark:text-neutral-200 leading-relaxed mb-8">
                I build custom blocks and Block Editor extensions for content-heavy WordPress sites — editorial tools that protect the design system and let non-technical teams publish.
              </p>
              <div className="flex flex-wrap gap-3 mb-10">
                {['Custom Blocks', 'React + PHP', 'Editorial Workflows'].map((stat) => (
                  <span key={stat} className="bg-primary-50 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-sm px-4 py-2 rounded-full font-medium border border-primary-200 dark:border-primary-700/50">
                    {stat}
                  </span>
                ))}
              </div>
              <a
                href="/gutenberg"
                className="inline-flex items-center bg-primary-600 dark:bg-primary-500 text-white font-semibold py-3 px-8 rounded-full hover:bg-primary-700 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all duration-300 group"
              >
                More on Gutenberg work
                <ArrowUpRight size={18} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </div>

            {/* Right column - Project preview cards (order-1 on mobile so images come first) */}
            <div className="space-y-6 order-1 lg:order-2">
              <a href="https://www.thepalmbeaches.com" target="_blank" rel="noopener noreferrer" className="group block bg-white dark:bg-neutral-700 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100 dark:border-neutral-600">
                <div className="h-48 overflow-hidden">
                  <img
                    src="/portfolio-images/palmbeaches.jpg"
                    alt="The Palm Beaches destination marketing website"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{objectPosition: 'top center'}}
                  />
                </div>
                <div className="p-4">
                  <div className="text-primary-600 dark:text-primary-400 text-xs font-semibold tracking-wide uppercase mb-1">The Palm Beaches</div>
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Destination Marketing Platform</h3>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">Custom Gutenberg blocks for 10+ regional tourism sites</p>
                </div>
              </a>
              <a href="https://discoveratlanta.com" target="_blank" rel="noopener noreferrer" className="group block bg-white dark:bg-neutral-700 rounded-2xl overflow-hidden shadow-soft hover:shadow-xl transition-all duration-300 hover:-translate-y-1 border border-primary-100 dark:border-neutral-600">
                <div className="h-48 overflow-hidden">
                  <img
                    src="/portfolio-images/atlanta.jpg"
                    alt="Discover Atlanta destination marketing website"
                    className="w-full h-full object-cover transition-transform duration-300 group-hover:scale-105"
                    style={{objectPosition: 'top center'}}
                  />
                </div>
                <div className="p-4">
                  <div className="text-primary-600 dark:text-primary-400 text-xs font-semibold tracking-wide uppercase mb-1">Discover Atlanta</div>
                  <h3 className="text-lg font-bold text-neutral-900 dark:text-white">Convention & Visitors Bureau</h3>
                  <p className="text-neutral-500 dark:text-neutral-400 text-sm mt-1">Block Editor extensions for Atlanta's official tourism site</p>
                </div>
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Maintenance & SEO */}
      <section className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">
            Maintenance & SEO
          </h2>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-10 lg:gap-14 items-center">
            {/* Left column - Description & CTA */}
            <div className="lg:col-span-2">
              <p className="text-lg text-neutral-700 dark:text-neutral-200 leading-relaxed mb-8">
                A website is only useful if people can find it and it keeps working. I offer monthly plans that keep a site updated, secure, and online, and search work that makes sure Google and AI assistants know exactly who you are.
              </p>
              <a
                href="/maintenance-and-seo"
                className="inline-flex items-center bg-primary-600 dark:bg-primary-500 text-white font-semibold py-3 px-8 rounded-full hover:bg-primary-700 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all duration-300 group"
              >
                See plans
                <ArrowUpRight size={18} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
              </a>
            </div>

            {/* Right column - What's covered */}
            <div className="lg:col-span-3 grid grid-cols-1 sm:grid-cols-3 gap-4">
              {[
                {
                  icon: <ShieldCheck size={22} />,
                  title: 'Maintenance',
                  items: ['Staging-tested updates', 'Security & malware scans', 'Daily backups & uptime', 'Bank of dev hours']
                },
                {
                  icon: <Search size={22} />,
                  title: 'SEO',
                  items: ['Technical SEO audit', 'Structured data (schema)', 'Google Business Profile', 'Directory cleanup']
                },
                {
                  icon: <Sparkles size={22} />,
                  title: 'AI search',
                  items: ['ChatGPT & Perplexity', "Google AI Overviews", 'Crawler access & citations', 'Monthly plain-language report']
                }
              ].map((card) => (
                <a
                  key={card.title}
                  href="/maintenance-and-seo"
                  className="group block bg-white dark:bg-neutral-800 p-5 rounded-2xl shadow-soft border border-primary-100 dark:border-neutral-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300"
                >
                  <div className="p-2.5 bg-primary-100 dark:bg-primary-900/40 rounded-xl w-fit mb-3 text-primary-600 dark:text-primary-400">
                    {card.icon}
                  </div>
                  <h3 className="text-lg font-bold mb-3 text-neutral-900 dark:text-white">{card.title}</h3>
                  <ul className="space-y-1.5">
                    {card.items.map((item) => (
                      <li key={item} className="text-sm text-neutral-600 dark:text-neutral-400 flex items-start gap-2">
                        <span className="mt-2 flex-shrink-0 w-1 h-1 rounded-full bg-primary-500 dark:bg-primary-400"></span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                </a>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Testimonials Section */}
      <Testimonials />

      {/* About Section */}
      <section id="about" className="py-24 px-6 bg-white dark:bg-neutral-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">About</h2>
          <div className="flex flex-col md:flex-row items-center gap-12">
            <div className="md:w-1/3">
              <div className="rounded-full w-48 h-48 sm:w-56 sm:h-56 md:w-64 md:h-64 overflow-hidden mx-auto shadow-soft ring-4 ring-primary-100 dark:ring-primary-800 ring-offset-4 dark:ring-offset-neutral-800">
                <img
                  src="/ben.jpeg"
                  alt="Benjamin Smith"
                  className="w-full h-full object-cover"
                />
              </div>
            </div>
            <div className="md:w-2/3">
              <p className="text-lg mb-8 dark:text-neutral-200">
                I've been building for the web since 2010 — 150+ projects for agencies, universities, brands, and banks. Lately that means destination platforms like Discover Atlanta and The Palm Beaches, kiosks for museums and trade shows, and ongoing care for the sites I build. Based in Wisconsin, working everywhere.
              </p>
              <div className="flex items-center gap-6">
                <a
                  href="/about"
                  className="inline-flex items-center bg-primary-600 dark:bg-primary-500 text-white font-semibold py-3 px-8 rounded-full hover:bg-primary-700 dark:hover:bg-primary-600 hover:shadow-lg hover:scale-105 transition-all duration-300 group"
                >
                  More about me
                  <ArrowUpRight size={18} className="ml-2 group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform" />
                </a>
                <div className="flex space-x-4">
                  <a
                    href="https://www.linkedin.com/in/benjaminchristophersmith/"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 rounded-full p-2"
                    aria-label="LinkedIn Profile"
                  >
                    <Linkedin size={28} />
                  </a>
                  <a
                    href="mailto:ben@bensmithdev.com"
                    className="text-neutral-600 dark:text-neutral-400 hover:text-primary-600 dark:hover:text-primary-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-primary-500 dark:focus:ring-primary-400 focus:ring-offset-2 dark:focus:ring-offset-neutral-800 rounded-full p-2"
                    aria-label="Email ben@bensmithdev.com"
                  >
                    <Mail size={28} />
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Skills Section */}
      <section id="skills" className="py-24 px-6 bg-neutral-50 dark:bg-neutral-900">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">Tools</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {skills.map((skillGroup, index) => (
              <div key={index} className="bg-white dark:bg-neutral-900 p-6 rounded-2xl shadow-soft border border-primary-100 dark:border-neutral-700 hover:shadow-xl hover:-translate-y-1 transition-all duration-300">
                <h3 className="text-xl font-bold mb-4 text-primary-700 dark:text-primary-400">{skillGroup.category}</h3>
                <div className="flex flex-wrap gap-2">
                  {skillGroup.items.map((skill, i) => (
                    <span key={i} className="bg-white dark:bg-neutral-800 text-neutral-700 dark:text-neutral-300 text-sm px-3 py-1.5 rounded-full border border-primary-200 dark:border-neutral-600 hover:border-primary-400 dark:hover:border-primary-500 hover:text-primary-700 dark:hover:text-primary-400 transition-all duration-200">
                      {skill}
                    </span>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="py-24 px-6 bg-primary-50 dark:bg-neutral-800">
        <div className="max-w-6xl mx-auto">
          <h2 className="text-3xl md:text-4xl font-bold mb-12 md:mb-16 text-center text-primary-700 dark:text-gray-100">Contact</h2>
          <p className="text-lg text-center -mt-6 mb-10 text-neutral-700 dark:text-neutral-200">Tell me what you're building.</p>
          <div className="bg-white dark:bg-neutral-800 p-8 rounded-2xl shadow-soft max-w-2xl mx-auto border border-primary-100 dark:border-neutral-700">
            <ContactForm />
          </div>
          <div className="text-center mt-12">
            <p className="text-lg dark:text-neutral-200">Or email me directly:</p>
            <a href="mailto:ben@bensmithdev.com" className="text-primary-600 dark:text-primary-400 font-semibold hover:text-accent-600 dark:hover:text-accent-400 transition-colors duration-200">ben@bensmithdev.com</a>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-neutral-900 dark:bg-black text-white py-12 px-6">
        <div className="max-w-6xl mx-auto">
          <div className="flex flex-col md:flex-row justify-between items-center">
            <div className="mb-6 md:mb-0">
              <a href="#" className="text-2xl font-bold">
                <span className="text-white">Ben Smith</span>
                <span className="text-orange-500"> Development</span>
              </a>
              <p className="mt-2 text-primary-200 dark:text-primary-300">Web platforms, kiosk software, interactive installations.</p>
            </div>
            <div className="flex space-x-6">
              <a 
                href="https://www.linkedin.com/in/benjaminchristophersmith/" 
                target="_blank" 
                rel="noopener noreferrer" 
                className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent-500 dark:focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-neutral-900 dark:focus:ring-offset-black rounded-full p-2"
                aria-label="LinkedIn Profile"
              >
                <Linkedin size={24} />
              </a>
              <a 
                href="mailto:ben@bensmithdev.com" 
                className="text-primary-300 dark:text-primary-400 hover:text-accent-300 dark:hover:text-accent-400 transition-all duration-200 hover:scale-110 focus:outline-none focus:ring-2 focus:ring-accent-500 dark:focus:ring-accent-400 focus:ring-offset-2 focus:ring-offset-neutral-900 dark:focus:ring-offset-black rounded-full p-2"
                aria-label="Email ben@bensmithdev.com"
              >
                <Mail size={24} />
              </a>
            </div>
          </div>
          <div className="border-t border-primary-700/30 dark:border-primary-600/30 mt-8 pt-8 text-center text-primary-300 dark:text-primary-400">
            <p>&copy; {new Date().getFullYear()} Benjamin Smith. All rights reserved.</p>
          </div>
        </div>
      </footer>
      </main>
      
      {/* BenBot Chatbot */}
      {/* <Suspense fallback={null}>
        <ChatInterface
          serverUrl={process.env.REACT_APP_CHAT_SERVER_URL || "http://localhost:1080"}
          userId={`guest-${Math.random().toString(36).substring(2, 9)}`}
          title="BenBot"
          layout="popup"
          darkMode={false}
          markdown={true}
          typingIndicator={false}
          timestampDisplay={false}
          avatarDisplay={false}
        />
      </Suspense> */}
      
    </div>
  );
};

export default Portfolio;